// src/auth/reportCentralFailure.ts
//
// Make a RAW-FETCH failure reach the same verdict as an RTK Query one.
//
// ── Why this exists ──────────────────────────────────────────────────────
//
// `store/api.ts` runs every RTK Query failure through
// `auth/centralFailureContract`, and a 401 SESSION_EXPIRED there ends the
// session and reaches Central login. But several surfaces call Central with a
// bare `fetch` instead, and those answers were never classified at all:
//
//   hooks/useNotificationPreferences.ts   -> `Failed to load preferences: 401`
//   context/NotificationProvider.tsx      -> a non-ok inbox read was DISCARDED
//
// Both talk straight to `CENTRAL_URL` with a bearer token, so a 401 from one of
// them is not exotic: it is the normal state of any tab older than fifteen
// minutes, because that is how long an access token lives. The result was
// precisely the bug this module exists to close: a terminal auth state surfaced
// as a generic product error ("Failed to load preferences: 401", for ever, with a
// Retry that cannot work), while the auth layer was never told anything had
// happened, so nothing sent the user to Central login.
//
// ── AND THE 15-MINUTE EXPIRY IS NOT A SESSION END ────────────────────────
//
// This module used to answer a routine TOKEN_EXPIRED exactly as it answered
// SESSION_EXPIRED: `centralLogout()`, which is a full-page Central round trip.
// That was the worse half of the same bug. `context/NotificationProvider.tsx`
// POLLS, so the poller normally noticed the expiry first and ended the session —
// unmounting the SPA and discarding unsaved work — before any page query could
// renew. A token expiry now renews silently through `auth/silentAuthorize.ts`,
// the same single-flight `store/api.ts` uses, and only a Central that has no
// live SSO session ends the session.
//
// Routing those failures through here gives them the SAME verdict and the SAME
// effects as the RTK path, which is what establishes the precedence: once auth
// is cleared, `AuthGuard` / `AdminGuard` withhold the page, so the component's
// generic error can no longer be what the user is left looking at.
//
// ── What it deliberately does NOT do ─────────────────────────────────────
//
// It never raises the full-screen Central-outage overlay. A single failing data
// read must not blank the whole app (same rule as `store/api.ts`), so an OUTAGE
// or a SERVICE_FAILURE verdict is handed back for the caller to render its own
// error — which is the correct, surviving purpose of those generic states.

import {
  classifyCentralFailure,
  type CentralFailureAction,
} from './centralFailureContract';
import { centralLogout } from '../store/slices/centralAuthSlice';
import { denialReasonFor, raiseBootDenial } from './bootDenial';
import { renewAccessTokenSilently } from './silentAuthorize';

/**
 * The minimum of a Redux dispatch this module needs.
 *
 * Deliberately `any`-shaped in the parameter position: react-redux's
 * `Dispatch<UnknownAction>` and RTK Query's `api.dispatch` are both assignable
 * to it, so this module can be called from a hook, from a base query, or from a
 * plain callback without either side importing the other's store types.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Dispatch = (action: any) => unknown;

export interface ReportCentralFailureInput {
  /** HTTP status, or a transport sentinel such as 'FETCH_ERROR'. */
  status: number | string;
  /** Parsed body, if one could be read. */
  body?: unknown;
  /** Where the failure came from, for the log line. */
  source: string;
}

/**
 * Classify one raw-fetch Central failure and carry out its verdict.
 *
 * Returns the action taken so a caller can decide whether its own error state is
 * still the right thing to show:
 *
 *   CENTRAL_LOGIN                        auth cleared — the caller must render
 *                                        NOTHING, the gates own the redirect.
 *   RETRY_AFTER_REFRESH                  a silent renewal was started and auth
 *                                        was LEFT ALONE — the caller must render
 *                                        NOTHING (not an error: nothing is
 *                                        wrong) and its effect re-runs itself
 *                                        when the new token lands.
 *   WORKSPACE_CLOSED / WORKSPACE_PICKER /
 *   APP_ACCESS_DENIED                    a boot-level surface was raised — the
 *                                        caller must render nothing.
 *   OUTAGE / SERVICE_FAILURE /
 *   PASS_THROUGH                         nothing was touched — the caller's own
 *                                        error state is correct.
 */
export function reportCentralFailure(
  dispatch: Dispatch,
  { status, body, source }: ReportCentralFailureInput,
): CentralFailureAction {
  const decision = classifyCentralFailure({ status, body });

  switch (decision.action) {
    // The session itself is over. Clearing auth IS the login transition; the
    // gates above render the one <Navigate to="/login">.
    case 'CENTRAL_LOGIN':
      console.warn(`[${source}] session over:`, decision.reason);
      dispatch(centralLogout());
      return decision.action;

    // The access token aged out (15 min). Renew it in the BACKGROUND against the
    // live Central session, through the same app-wide single-flight the RTK
    // Query path uses, so a polling read and a page's queries can never start
    // two authorizes. Auth is deliberately NOT cleared: nothing here says the
    // session is over, and only the renewal's own verdict may say so.
    case 'RETRY_AFTER_REFRESH':
      console.warn(`[${source}] access token expired — renewing silently:`, decision.reason);
      void renewAccessTokenSilently(dispatch).then((outcome) => {
        if (!outcome.ok && outcome.disposition === 'SESSION_OVER') {
          console.warn(`[${source}] silent renewal refused — session over:`, outcome.reason);
          dispatch(centralLogout());
        }
      });
      return decision.action;

    case 'WORKSPACE_CLOSED':
    case 'WORKSPACE_PICKER':
    case 'APP_ACCESS_DENIED':
      console.warn(`[${source}] workspace/app refusal:`, decision.reason);
      // The overlay is worded from the reason (BH-06): an access denial is not
      // a closed workspace.
      raiseBootDenial(dispatch, denialReasonFor(decision.action) ?? 'workspace_closed', decision.code, body);
      return decision.action;

    case 'OUTAGE':
    case 'SERVICE_FAILURE':
    case 'PASS_THROUGH':
    default:
      return decision.action;
  }
}

/**
 * True when the caller must NOT render its own error state, because a verdict
 * with its own surface has already been reached.
 */
export function terminalForCaller(action: CentralFailureAction): boolean {
  return (
    action === 'CENTRAL_LOGIN' ||
    action === 'RETRY_AFTER_REFRESH' ||
    action === 'WORKSPACE_CLOSED' ||
    action === 'WORKSPACE_PICKER' ||
    action === 'APP_ACCESS_DENIED'
  );
}

// ──────────────────────────────────────────────────────────────────────────
// The RAW-FETCH choke points.
//
// Three service modules call Central browser-direct and every one of them ended
// its non-ok path the same way:
//
//     throw new Error(message || `Request failed: ${res.status}`);
//
// bypassing the classifier completely, so a terminal 401 became
// "Failed to load members" / "Failed to load invites" / "Failed to load billing
// status" / "Failed to load" — permanently, with no login transition, because
// the auth layer was never told. The modules are pure (no React, no store), so
// the dispatch is registered once from `store/index.ts` rather than threaded
// through every caller.
//
// WHICH SERVICE EMITS WHICH CODE, verified against Central's source rather than
// assumed: Central emits TOKEN_EXPIRED, INVALID_TOKEN, WORKSPACE_CLOSED,
// AUTH_REQUIRED, SESSION_REVOKED, SESSION_EXPIRED, MEMBERSHIP_INACTIVE and
// MEMBERSHIP_REVOKED. It NEVER emits APP_ACCESS_DENIED (an app-BFF code) or
// ACCESS_TOKEN_EXPIRED (the suite contract's spelling). So on a browser-direct
// Central call the denial family that actually arrives is MEMBERSHIP_*, and an
// app that classified only APP_ACCESS_DENIED would miss every real denial.
// ──────────────────────────────────────────────────────────────────────────

let registeredDispatch: Dispatch | null = null;

/**
 * Register the store's dispatch for the pure service modules. Called once, from
 * `store/index.ts`, which already owns the store and imports everything else.
 */
export function setCentralFailureDispatch(dispatch: Dispatch): void {
  registeredDispatch = dispatch;
}

/** Test seam. */
export function __resetCentralFailureDispatch(): void {
  registeredDispatch = null;
}

/**
 * Thrown in place of a plain `Error` when a verdict with its own surface has
 * already been raised, so a `catch` that sets generic error copy can recognise it
 * and stay silent.
 */
export class CentralVerdictHandled extends Error {
  readonly action: CentralFailureAction;
  constructor(action: CentralFailureAction) {
    super(`Central verdict handled: ${action}`);
    this.name = 'CentralVerdictHandled';
    this.action = action;
  }
}

/**
 * Classify one failure against the REGISTERED dispatch and carry out its
 * verdict, without deciding what the caller should throw.
 *
 * `throwClassifiedCentralFailure` is the right call for a site whose only
 * non-auth answer is a plain `Error`. This is for a site that has ALREADY read
 * the body and has its own richer error type to throw when the failure turns
 * out not to be a session verdict — `hooks/useVoicePractice.ts` throws a
 * `RecordingUploadError` carrying the server's status and stable code, so a
 * usage limit can still be worded as a limit. Such a caller must not be forced
 * to re-read the body (a `Response` body can only be consumed once) or to lose
 * its code to a flattened string, which is the whole bug this module exists to
 * close.
 *
 * Returns the action taken, or `null` when no dispatch is registered and
 * nothing could be classified — in which case the caller's own error is the
 * only honest thing to show.
 */
export function applyRegisteredCentralFailure(
  input: ReportCentralFailureInput,
): CentralFailureAction | null {
  if (!registeredDispatch) {
    console.warn(`[${input.source}] no dispatch registered — Central failure not classified`);
    return null;
  }
  return reportCentralFailure(registeredDispatch, input);
}

/**
 * The one-liner for a raw-fetch site: classify, carry out the verdict, and throw
 * either the sentinel (a surface was raised — say nothing) or the caller's own
 * error.
 *
 * With no dispatch registered it degrades to the caller's own error rather than
 * throwing: a missing registration must never turn into a silent blank screen.
 */
export async function throwClassifiedCentralFailure(
  res: Response,
  source: string,
  fallbackMessage?: string,
): Promise<never> {
  const body = (await res.json().catch(() => undefined)) as Record<string, unknown> | undefined;

  const action = applyRegisteredCentralFailure({ status: res.status, body, source });
  if (action !== null && terminalForCaller(action)) throw new CentralVerdictHandled(action);

  const message =
    typeof body?.message === 'string'
      ? body.message
      : fallbackMessage || `Request failed: ${res.status}`;
  throw new Error(message);
}
